import type { AmmunitionDefinition, AmmunitionEffectResult } from '../types/ammunition';
import { UNUSUAL_AMMUNITION } from '../data/unusual-ammunition';

/**
 * Static service for ammunition data access, compatibility validation, and effect resolution.
 * Reads from the UNUSUAL_AMMUNITION registry and computes effective weapon stat modifiers.
 */
export class AmmunitionService {
  /** Get all 9 ammunition definitions */
  static getAllDefinitions(): AmmunitionDefinition[] {
    return UNUSUAL_AMMUNITION;
  }

  /** Get a single definition by ID */
  static getDefinition(ammoId: string): AmmunitionDefinition | undefined {
    return UNUSUAL_AMMUNITION.find((def) => def.id === ammoId);
  }

  /** Get ammo types compatible with a weapon (by sourceId) */
  static getCompatibleAmmo(weaponSourceId: string): AmmunitionDefinition[] {
    return UNUSUAL_AMMUNITION.filter((def) => def.compatibleWeapons.includes(weaponSourceId));
  }

  /** Check if a specific ammo type is compatible with a weapon */
  static isCompatible(ammoId: string, weaponSourceId: string): boolean {
    const def = this.getDefinition(ammoId);
    if (!def) return false;
    return def.compatibleWeapons.includes(weaponSourceId);
  }

  /** Apply ammo effects, returning computed modifiers */
  static applyEffects(ammoId: string): AmmunitionEffectResult | null {
    const def = this.getDefinition(ammoId);
    if (!def) return null;

    const { effects } = def;

    return {
      ammoName: def.name,
      damageBonus: effects.damageModifier ?? 0,
      penetration: effects.penetrationModifier ?? null,
      attackModifier: effects.attackModifier ?? 0,
      damageTypeOverride: effects.damageTypeOverride ?? null,
      qualitiesAdded: effects.qualitiesAdded ?? [],
      qualitiesRemoved: effects.qualitiesRemoved ?? [],
      clipOverride: effects.clipOverride ?? null,
      conditionalEffects: effects.conditionalEffects ?? [],
      doubleArmour: effects.doubleArmour ?? false
    };
  }
}
